
import React from 'react';
import { Col, Table, Modal } from 'react-bootstrap';
import {
  Card, CardBody, CardTitleWrap, CardTitle,
} from '@/shared/components/Card';
import toast from 'react-hot-toast';
import { MdEditNote, MdZoomOutMap, MdZoomInMap } from 'react-icons/md';
import PropTypes from 'prop-types';
import { Button } from '../../../shared/components/Button';
import { AuthContext } from '../../../context/AuthContext';
import ConfirmationModal from './ConfirmationModal';

const TemplateTable = ({
 templateRefresh, setZoomIn, zoomIn, setTemplateRefresh, 
}) => {
  const [templates, setTemplates] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const { apiGlobal } = React.useContext(AuthContext);

  const [varCount, setVarCount] = React.useState(0);
  const [templateContent, setTemplateContent] = React.useState('');

  const [showDeleteModal, setShowDeleteModal] = React.useState({ show: false, id: '' });
  const [showEditModal, setShowEditModal] = React.useState({
    show: false,
    name: '',
    message: '',
    id: '',
  });

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const url = await fetch(`${apiGlobal}/template_master_data.php`, { method: 'post' });
      const response = await url.json();
      if (response.status && response.message === 'Success') {
        setTemplates(response?.data || []);
      } else {
        toast.error(response.message);
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchTemplates();
  }, [templateRefresh]);

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    if (!showEditModal.name || !showEditModal.message || !showEditModal.id) {
      toast.error('Please fill in all fields correctly');
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('name', showEditModal.name);
    formData.append('message', showEditModal.message);
    formData.append('id', showEditModal.id);

    try {
      const url = await fetch(`${apiGlobal}template_master_update.php`, {
        method: 'post',
        body: formData,
      });
      const response = await url.json();
      if (response.status && response.message === 'Template updated successfully.') {
        setShowEditModal({
 show: false, name: '', message: '', id: '', 
});
        setTemplateRefresh(prev => !prev);
        toast.success('Template updated successfully');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error('Error updating template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    if (!showDeleteModal.id) {
      toast.error('Invalid template selection');
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('id', showDeleteModal.id);

    try {
      const url = await fetch(`${apiGlobal}template_master_delete.php`, {
        method: 'post',
        body: formData,
      });
      const response = await url.json();
      if (response.status && response.message === 'Template deleted successfully.') {
        setShowDeleteModal({ show: false, id: '' });
        setTemplateRefresh(prev => !prev);
        toast.success('Template deleted successfully');
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    } finally {
      setLoading(false);
    }
  };

  const countVariables = (content) => {
    const regex = /\{\{(\d+)\}\}/g;
    const matches = [...content.matchAll(regex)];
    const numbers = matches.map(match => parseInt(match[1], 10));
    return numbers.length > 0 ? Math.max(...numbers) : 0;
  };

  const handleAddVariable = () => {
    const nextVar = varCount + 1;
    const newVariable = `{{${nextVar}}}`;
    const updatedContent = showEditModal.message + newVariable;
    setShowEditModal(prev => ({ ...prev, message: updatedContent }));
    setVarCount(nextVar);
  };

  return (
    <div>
      <Col md={12}>
        <Card>
          <CardBody>
            <CardTitleWrap>
              <CardTitle>
                Whatsapp Templates
                <span style={{ float: 'right', cursor: 'pointer' }}>
                  {zoomIn ? (
                    <MdZoomInMap fontSize="20px" onClick={() => setZoomIn(false)} />
                  ) : (
                    <MdZoomOutMap fontSize="20px" onClick={() => setZoomIn(true)} />
                  )}
                </span>
              </CardTitle>
            </CardTitleWrap>
            {/* eslint-disable-next-line */}
            {loading ? (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center">
                <p>No templates found.</p>
              </div>
            ) : (
              <Table
                responsive 
                style={{ backgroundColor: '#f9f9f9', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
              >
                <thead className="bg-success text-white text-center">
                  <tr>
                    <th>#</th>
                    <th>Template Name</th>
                    <th>Template Content</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((template, index) => (
                    <tr key={template.id || index} className="text-center">
                      <td>{index + 1}</td>
                      <td>{template.name}</td>
                      <td>
                        <textarea className="form-control" rows="2" value={template.message} readOnly />
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                          const varCountInit = countVariables(template.message);
                          setVarCount(varCountInit);
                          setShowEditModal({
                            show: true,
                            name: template.name,
                            message: template.message,
                            id: template.id,
                          });
                        }}
                        >Edit
                        </Button>
                        {' '}
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                          setShowDeleteModal({ show: true, id: template.id });
                        }}
                        >Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </Col>

      <Modal
        show={showEditModal.show}
        onHide={() => setShowEditModal({
 show: false, name: '', message: '', id: '', 
})}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleEditSubmit}>
            <div className="mb-3">
              <label htmlFor="template-name" className="form-label">Template Name</label>
              <input
                type="text"
                className="form-control"
                id="template-name"
                value={showEditModal.name}
                onChange={e => setShowEditModal({ ...showEditModal, name: e.target.value })}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="template-content" className="form-label">Template Content</label>
              <textarea
                className="form-control"
                id="template-content"
                rows="3"
                value={showEditModal.message}
                onChange={(e) => {
                  const newMsg = e.target.value;
                  setShowEditModal({ ...showEditModal, message: newMsg });
                  setVarCount(countVariables(newMsg));
                }}
                required
              />
              <div className="d-flex">
                <Button variant="warning" type="button" onClick={handleAddVariable} className="mt-2">
                  Add Variable
                </Button>
                <Button variant="primary" type="submit" disabled={loading} className="mt-2">
                  Edit Template
                </Button>
              </div>
             
            </div>
           
          </form>
        </Modal.Body>
      </Modal>

      <ConfirmationModal
        show={showDeleteModal.show}
        handleClose={() => setShowDeleteModal({ show: false, id: '' })}
        handleDelete={handleConfirmDelete}
        header="Confirm Delete"
        message="Are you sure you want to delete this template?"
        buttonType="danger"
        buttonText="Delete Template"
      />
    </div>
  );
};

TemplateTable.propTypes = {
  setZoomIn: PropTypes.func.isRequired,
  zoomIn: PropTypes.bool.isRequired,
  templateRefresh: PropTypes.bool.isRequired,
  setTemplateRefresh: PropTypes.func.isRequired,
};

export default TemplateTable;

// import React from 'react';
// import { Col, Table, Modal } from 'react-bootstrap';
// import {
//   Card, CardBody, CardTitleWrap, CardTitle,
// } from '@/shared/components/Card';
// import toast from 'react-hot-toast';
// import { MdEditNote, MdZoomOutMap, MdZoomInMap } from 'react-icons/md';
// import PropTypes from 'prop-types';
// import { Button, ButtonGroup } from '../../../shared/components/Button';
// import { AuthContext } from '../../../context/AuthContext';
// import ConfirmationModal from './ConfirmationModal';




// const TemplateTable = ({
//  templateRefresh, setZoomIn, zoomIn, setTemplateRefresh, 
// }) => {
//     const [templates, setTemplates] = React.useState([]);
//     const [loading, setLoading] = React.useState(false);
//     const { apiGlobal } = React.useContext(AuthContext);
//     const [showDelteModal, setShowDeleteModal] = React.useState({
//         show: false,
//         id: '',
//     });
//     const [showEditModal, setShowEditModal] = React.useState({
//         show: false,
//         name: '',
//         message: '',
//         id: '',
//     });


//     const fetchTemplates = async () => {
//         setLoading(true);
//         try {
//             const url = await fetch(`${apiGlobal}/template_master_data.php`, {
//                 method: 'post',
//             });
//             const response = await url.json();
//             if (response.status && response.message === 'Success') {
//                 setTemplates(response?.data || []);
//             } else {
//                 toast.error(response.message);
//                 setTemplates([]);
//             }
//         } catch (error) {
//             console.error('Error fetching templates:', error);
//         } finally {
//             setLoading(false);
//         }
//     };

//     React.useEffect(() => {
//         fetchTemplates();
//     }, [templateRefresh]);


//      const handleEditSubmit = async (event) => {
//     event.preventDefault();
//     setLoading(true);
//     // const templateName = document.getElementById('template-name').value;
//     // const templateContent = document.getElementById('template-content').value;
//     // console.log(showEditModal.name, showEditModal.message);

//     if (!showEditModal.name || !showEditModal.message) {
//       toast.error('Please fill in all fields');
//       setLoading(false);
//       return;
//     }
//     if (!showEditModal.id) {
//       toast.error('Please select a template to edit');
//         setLoading(false);
//         return;
//     }

//     const formData = new FormData();
//     formData.append('name', showEditModal.name);
//     formData.append('message', showEditModal.message);
//     formData.append('id', showEditModal.id);

//     try {
//       const url = await fetch(`${apiGlobal}template_master_update.php`, {
//         method: 'post',
//         body: formData,
//       });
//       const response = await url.json();
//       if (response.status && response.message === 'Template updated successfully.') {
//         // document.getElementById('template-name').value = '';
//         // document.getElementById('template-content').value = '';
//         setShowEditModal({
//             show: false,
//             name: '',
//             message: '',
//             id: '',
//         });
//         setTemplateRefresh(prev => !prev);
//         toast.success('Template Edit successfully');
//       } else {
//         toast.error(response.message);
//         // alert('Error adding template');
//       }
//     } catch (error) {
//       console.error('Error adding template:', error);
//     } finally {
//       setLoading(false);
//     }
//   };
     
//    const handleConfirmDelete = async () => {
//     setLoading(true);
//     if (!showDelteModal.id) {
//       toast.error('Please select a template to delete');
//       setLoading(false);
//       return;
//     }
//     const formData = new FormData();
//     formData.append('id', showDelteModal.id);

//     try {
//       const url = await fetch(`${apiGlobal}template_master_delete.php`, {
//         method: 'post',
//         body: formData,
//       });
//       const response = await url.json();
//       if (response.status && response.message === 'Template deleted successfully.') {
//         setShowDeleteModal({
//             show: false,
//             id: '',
//         });
//         setTemplateRefresh(prev => !prev);
//         toast.success('Template deleted successfully');
//       } else {
//         toast.error(response.message);
//       }
//     } catch (error) {
//       console.error('Error deleting template:', error);
//     } finally {
//       setLoading(false);
//     }
//    };


//     return (
//       <div>
//         <Col md={12}>
//           <Card>
//             <CardBody>
//               <CardTitleWrap>
//                 <CardTitle>
//                   Whatsapp Templates
//                   <p style={{ float: 'right' }}>

//                     {!zoomIn ? (
//                       <MdZoomOutMap 
//                         fontSize="20px"
//                         onClick={() => setZoomIn(true)}
//                         style={{ cursor: 'pointer' }}
//                       />
// )
//                         : (
//                           <MdZoomInMap
//                             fontSize="20px"
//                             onClick={() => setZoomIn(false)}
//                             style={{ cursor: 'pointer' }}
//                           />
// )}  
//                   </p>
//                 </CardTitle>
//               </CardTitleWrap>
//               {/* eslint-disable-next-line */}
//               {loading ? (
//                 <div className="text-center">
//                   <div className="spinner-border" role="status">
//                     <span className="visually-hidden">Loading...</span>
//                   </div>
//                 </div>
//                 ) : templates.length === 0 ? (
//                   <div className="text-center">
//                     <p>No templates found.</p>
//                   </div>
//                 ) : (
//                   <Table
//                     responsive
//                     style={{
//                     backgroundColor: '#f9f9f9',
//                     borderRadius: '8px',
//                     boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
//                     }}
//                   >
//                     <thead className="bg-success" style={{ color: 'white', textAlign: 'center' }}>
//                       <tr>
//                         <th>#</th>
//                         <th>Template Name</th>
//                         <th>Template Content</th>
//                         <th>Action</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {templates.map((template, index) => (
//                         <tr key={template.id || index} style={{ textAlign: 'center' }}>
//                           <td>{index + 1}</td>
//                           <td>{template.name}</td>
//                           <td>
//                             <textarea 
//                               className="form-control"
//                               rows="2"
//                               placeholder="Enter template content"
//                               value={template.message}
//                               readOnly
//                             />
//                           </td>
//                           <td>
//                             {/* <ButtonGroup> */}
//                             <Button 
//                               variant="outline-primary"
//                               size="sm"
//                               onClick={() => {
//                                 setShowEditModal({
//                                     show: true,
//                                     name: template.name,
//                                     message: template.message,
//                                     id: template.id,
//                                 });
//                                 }}
//                             >Edit
//                             </Button>
//                             <Button 
//                               variant="outline-danger"
//                               size="sm"
//                               onClick={() => {
//                                 setShowDeleteModal({
//                                     show: true,
//                                     id: template.id,
//                                 });
//                               }}
//                             >Delete
//                             </Button>
//                             {/* </ButtonGroup> */}
                           
//                           </td>
//                         </tr>
//                     ))}
//                     </tbody>
//                   </Table>
//                 )}
//             </CardBody>
//           </Card>
//         </Col>
//         <Modal 
//           show={showEditModal?.show}
//           onHide={() => setShowEditModal({
//                                     show: false,
//                                     name: '',
//                                     message: '',
//                                     id: '',
//                                 })}
//           centered
//           size="md"
//         >
//           <Modal.Header
//             closeButton
//             // closeVariant={currentTheme === 'Dark' && 'white'}
//           >
//             <Modal.Title>Edit Template</Modal.Title>
//           </Modal.Header>
//           <Modal.Body>
//             <form onSubmit={handleEditSubmit}>
//               <div className="mb-3">
//                 <label htmlFor="template-name" className="form-label">Template Name</label>
//                 <input 
//                   type="text"
//                   className="form-control"
//                   id="template-name"
//                   placeholder="Enter template name"
//                   value={showEditModal.name}
//                   onChange={e => setShowEditModal({ ...showEditModal, name: e.target.value })}
//                   required
//                 />
//               </div>
//               <div className="mb-3">
//                 <label htmlFor="template-content" className="form-label">Template Content</label>
//                 <textarea 
//                   className="form-control"
//                   id="template-content"
//                   rows="3"
//                   value={showEditModal.message}
//                   onChange={e => setShowEditModal({ ...showEditModal, message: e.target.value })}
//                   placeholder="Enter template content"
//                   required
//                 />
//               </div>
//               <Button
//                 variant="primary" 
//                 type="submit"
//                 className="btn btn-primary"
//                 disbled={loading}
//               >Edit template
//               </Button>
//             </form>
//           </Modal.Body>
//         </Modal>
//         <ConfirmationModal 
//           show={showDelteModal.show}
//           handleClose={() => setShowDeleteModal({
//             show: false,
//             id: '',
//           })}
//           handleDelete={handleConfirmDelete}
//           header="Confirm Delete"
//           message="Are you sure you want to Delete this template?"
//           buttonType="danger"
//           buttonText="Delete Template"
//         />
//       </div>
//   );
// };

// TemplateTable.propTypes = {
//     setZoomIn: PropTypes.func.isRequired,
//     zoomIn: PropTypes.bool.isRequired,
//     templateRefresh: PropTypes.bool.isRequired,
//     setTemplateRefresh: PropTypes.func.isRequired,
// };

// export default TemplateTable;
